import { css, cx } from '@emotion/css';
import { HTMLAttributes } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { Button, Tooltip } from 'antd';
import productSvg from './assets/product.svg';
import storeSvg from './assets/store.svg';
import styled from '@emotion/styled';
import { VoucherProductPreviewer } from './VoucherProductPreviewer';
import Copy from 'views/Conversation/Sider/Copy';
import { useVoucherCardDisplay, VoucherCardTagInfo } from './useVoucherCardDisplay';
import { getCouponTypeLabel } from 'services/i18n/enum/coupon';

interface VoucherCardProps extends HTMLAttributes<HTMLDivElement> {
  voucher: Conversation.Voucher.Vo;
  channel?: General.PlatformUpperCaseTunnel;
  storeId: Conversation.Conversation['storeId'];
  mask?: boolean;
  before?: React.ReactNode;
  after?: React.ReactNode;
}

const typeIconMap = {
  1: storeSvg,
  2: productSvg,
};

/** 根据 hook 返回的 tag 信息渲染状态标签 */
function VoucherStatusTag({ tag }: { tag: VoucherCardTagInfo }) {
  const { t } = useTranslate();
  const label = t(tag.labelKey);
  if (tag.status === 'ENDED') {
    return (
      <GreyTag color="#f0f2f5" className="!text-[#515359]">
        {label}
      </GreyTag>
    );
  }
  if (tag.status === 'SCHEDULED') {
    return (
      <PurpleTag color="#d7e1fa" className="!#336fff">
        {label}
      </PurpleTag>
    );
  }
  return (
    <GreenTag color="#d7f5e8" className="!#00bf69">
      {label}
    </GreenTag>
  );
}

export const VoucherCard = (props: VoucherCardProps) => {
  const { className, voucher, storeId, mask, before, after, ...restProps } = props;
  const { t } = useTranslate();
  const display = useVoucherCardDisplay({ voucher });

  const {
    voucherName,
    voucherType,
    tag,
    discount,
    dateText,
    showProductPreviewer,
    showVoucherCode,
    voucherCode,
    showUsageAmount,
    usageQuantity,
    currentUsage,
    usageAmountHighlight,
    showPerCustomerLimit,
    perCustomerLimit,
    showCollectStartTime,
    collectStartTimeFormatted,
    showMinBasketPrice,
    minBasketPriceText,
    showMaxPriceDetail,
    maxPriceText,
    showDisplayStartTime,
    displayStartTimeFormatted,
    showShortDescription,
    shortDescriptionText,
    showMinPurchaseQuantity,
    minPurchaseQuantityText,
  } = display;

  const couponTypeLabel = getCouponTypeLabel(t, voucherType);

  return (
    <div
      className={cx('flex', 'bg-[var(--fett-bg-elevated)]', mask ? maskCss : undefined, className)}
      {...restProps}
    >
      {before}
      <div className="flex-grow flex-shrink-0 basis-0 flex flex-col justify-center overflow-hidden">
        <div className="flex-grow flex-shrink-0 basis-0 flex flex-col pt-2.5 pr-3.5 pb-2.5 pl-0 border-t-0 border-r-0 border-l-0 border-b border-dashed border-[var(--fett-streak-grey)]">
          <div className="flex items-center overflow-hidden gap-1">
            <Tooltip title={couponTypeLabel}>
              <img className="w-4 h-4" src={typeIconMap[voucherType]} alt="no info" />
            </Tooltip>
            <div
              className="flex-grow flex-shrink-0 basis-0 overflow-hidden text-ellipsis whitespace-nowrap text-[var(--fett-text-secondary)] text-sm"
              title={voucherName}
            >
              {voucherName}
            </div>
            {tag ? <VoucherStatusTag tag={tag} /> : null}
          </div>
          <div className="flex items-center justify-between">
            {discount ? (
              <Reward>
                {discount.mainText}
                {discount.subText ? (
                  <span className="inline-block ml-1 text-xs">{discount.subText}</span>
                ) : null}
              </Reward>
            ) : null}
            {showProductPreviewer && (
              <VoucherProductPreviewer storeId={storeId} voucher={voucher}>
                <Button size="small">{t('domain.coupon.field.eligibleProducts')}</Button>
              </VoucherProductPreviewer>
            )}
          </div>
          <div className="flex flex-col">
            {showShortDescription && <Detail>{shortDescriptionText}</Detail>}
            {showVoucherCode && (
              <Detail>
                {voucherCode}
                <Copy
                  className="ml-1"
                  text={voucherCode}
                  successMessage={t('common.feedback.copySuccess')}
                  failedMessage={t('common.feedback.copyFailed')}
                />
              </Detail>
            )}
            {showUsageAmount && (
              <Detail>
                <T
                  keyName="scene.chatWorkspace.couponTab.label.claimedRatio"
                  params={{
                    total: usageQuantity,
                    used: currentUsage,
                    comx: (
                      <span
                        style={{
                          color: usageAmountHighlight
                            ? 'var(--fett-surface-error-text)'
                            : undefined,
                        }}
                      />
                    ),
                  }}
                />
              </Detail>
            )}
            {showPerCustomerLimit && (
              <Detail>
                {t('domain.coupon.field.usageLimitPerUser')}: {perCustomerLimit}
              </Detail>
            )}
            {showCollectStartTime && (
              <Detail>
                <div>{t('domain.coupon.field.claimPeriod')}:</div>
                <div>{collectStartTimeFormatted}</div>
              </Detail>
            )}
            {showMinBasketPrice && (
              <Detail>
                {t('domain.coupon.field.minSpend')}: {minBasketPriceText}
              </Detail>
            )}
            {showMinPurchaseQuantity && (
              <Detail>
                {t('domain.coupon.field.minPurchaseQuantity')}: {minPurchaseQuantityText}
              </Detail>
            )}
            {showMaxPriceDetail && (
              <Detail>
                {t('domain.coupon.field.maxDiscount')}: {maxPriceText}
              </Detail>
            )}
            {showDisplayStartTime && (
              <Detail>
                {t('domain.coupon.field.enablePreview')}: {displayStartTimeFormatted}
              </Detail>
            )}
          </div>
        </div>
        <div className="w-full h-[30px] leading-[30px] text-xs text-[var(--fett-text-tertiary)]">
          {dateText}
        </div>
      </div>
      {after}
    </div>
  );
};

const maskCss = css`
  mask: radial-gradient(circle at 8px 8px, transparent 8px, red 0) -128.5px -8px / 100%;
`;

const Reward = styled.div`
  line-height: 32px;
  font-size: 16px;
  font-weight: 500;
  color: var(--fett-colors-price);
`;

const Detail = styled.div`
  margin-bottom: 4px;
  line-height: 20px;
  font-size: 12px;
  color: var(--fett-text-tertiary);
  word-break: break-all;
`;

const Tag = styled.span`
  flex: 0 0 auto;
  line-height: 24px;
  padding: 0 8px;
  font-size: 12px;
  border-radius: 2px;
`;

const GreenTag = styled(Tag)`
  background: var(--fett-scenario-success-bg);
  color: var(--fett-scenario-success-text);
`;

const PurpleTag = styled(Tag)`
  background: var(--fett-scenario-accent-strong-bg);
  color: var(--fett-scenario-accent-text);
`;

const GreyTag = styled(Tag)`
  background: var(--fett-border-default);
  color: var(--fett-text-secondary);
`;
